import type { FunctionComponent, PropsWithChildren } from 'react'
import React, { createContext, useContext, useMemo, useRef, useState } from 'react'

import { noop } from './noop'

export interface OverflowContextInterface {
  overflowed: boolean
  isOverflowed: () => boolean
  increment: () => void
  decrement: () => void
}

const defaultContextValue: OverflowContextInterface = {
  overflowed: false,
  isOverflowed: () => false,
  increment: noop,
  decrement: noop,
}

const Context = createContext<OverflowContextInterface>(defaultContextValue)

/** test only export */
export const __OverflowContext = Context

export const OverflowProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  if (!IS_BROWSER) {
    return <Context value={defaultContextValue}>{children}</Context>
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const countRef = useRef(0)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [overflowed, setOverflowed] = useState(false)

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const context = useMemo(
    () => ({
      overflowed,
      isOverflowed: () => countRef.current > 0,
      increment: () => {
        countRef.current++

        if (countRef.current > 0) {
          setOverflowed(true)
          document.body.style.overflow = 'hidden'
        }
      },
      decrement: () => {
        countRef.current--

        if (countRef.current <= 0) {
          setOverflowed(false)
          document.body.style.removeProperty('overflow')
        }
      },
    }),
    [overflowed]
  )

  return <Context value={context}>{children}</Context>
}

export const useOverflowContext = () => useContext(Context)
